// src/components/dashboard/TranscriptsWidget.js
// A small admin-dashboard card for the call-transcript library: count + the most
// recent transcript's sourceLabel and date, linking to /transcripts. Fails soft
// (shows 0 / awaiting) so a missing list endpoint never breaks the dashboard.

import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { THEME } from '../../pages/enterprise-ai/genesisData';
import { authHeader, T6_BASE } from '../../hooks/useGtm';

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function TranscriptsWidget() {
  const [count, setCount] = useState(null);
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeader();
        const res = await axios.get(`${T6_BASE}/api/admin/genesis/transcripts`, { headers });
        const list = Array.isArray(res.data && res.data.transcripts) ? res.data.transcripts : [];
        if (!cancelled) {
          setCount(list.length);
          setRecent(list[0] || null);
        }
      } catch {
        if (!cancelled) {
          setCount(0);
          setRecent(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      component={RouterLink}
      to="/transcripts"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 2.5,
        borderRadius: '12px',
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        color: THEME.textPrimary,
        textDecoration: 'none',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: THEME.mint },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <DescriptionOutlinedIcon sx={{ color: THEME.mint, fontSize: 20 }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Call transcripts
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.1 }}>
        {count == null ? '—' : count.toLocaleString('en-US')}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mt: 'auto', pt: 1 }}>
        {recent
          ? `Most recent: ${recent.sourceLabel || 'Transcript'}${recent.uploadedAt ? ` · ${fmtDate(recent.uploadedAt)}` : ''}`
          : 'No transcripts yet'}
      </Typography>
    </Box>
  );
}
